// Import Dependencies
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import axios from 'axios';

// Components
import Section from '../Section';
import SideBar from '../SideBar';
import Header from '../Header';
import FilterBar from '../FilterBar';
import ConcertCardList from '../ConcertCardList';

import ConcertsGroupedByMonth from '../../../components/CreateYourOwnPackage/components/ConcertsGroupedByMonth';
import ConcertsSortedByDate from '../../../components/CreateYourOwnPackage/components/ConcertsSortedByDate';
import Modal from '../../../components/CreateYourOwnPackage/components/Modal';

// Import Image
import Loader from '../../../../web/images/svg/loader.svg';
import Logo from '../../../../web/images/svg/logo-calendar.svg';

// Import Utilities
import {
  formatArrayOfObject,
  removeQueryStringParams,
  convertTimestampToDate,
  removeItemFromArray,
  resetCheckbox,
} from '../../../utils';

import getMonthName from './getMonthName';
import getMonths from './getMonths';
import { updateLocationQuery } from '../../../utils/updateLocationQuery';

const defaultFilterQueries = {
  concertType: [],
  time: [],
  venue: [],
  concertMood: [],
  concertSeason: [],
  concertInstrument: [],
  concertSoloist: [],
  concertComposer: [],
  day: [],
  month: [],
  year: [],
};

let filterQueries = structuredClone(defaultFilterQueries); // for filters

const urlParams = new URLSearchParams(window.location.search);
Object.keys(filterQueries).forEach(key => {
  const paramValue = urlParams.get(key);

  if (paramValue) {
    filterQueries[key] = paramValue.split(',');
  }
});

const buildSearchQueryFromFilters = (filters) => {
  const query = [];

  Object.keys(filters)
    .forEach((key) => {
      if (filters[key].length) {
        const keyValue = filters[key].join(',');
        query.push(encodeURIComponent(key) + '=' + (keyValue)); // eslint-disable-line
      }
    });

  return query.join('&');
};

const currentFilters = JSON.parse(JSON.stringify(filterQueries));

const Calendar = ({ cyop, setModal }) => {
  const calendarWasPreopened = cyop ? false : window.location.hash === '#calendar';

  let apiMetaUrl = '';
  let queriedIds = [];

  if (cyop) {
    apiMetaUrl = '/tessitura/listOfConcerts';
  } else {
    const MOUNT_NODE = document.querySelector('.js-calendar-content');
    ({ apiMetaUrl } = MOUNT_NODE.dataset);
  }

  // States
  const [apiUrl, setApiUrl] = useState(`${apiMetaUrl}?${buildSearchQueryFromFilters(currentFilters)}`);
  const [concerts, setConcerts] = useState([]);
  const [error, setError] = useState('');
  const [filterQueriesState, setFilterQueriesState] = useState(JSON.stringify(currentFilters)); // for URL params
  const [hasCriticalError, setHasCriticalError] = useState(false);
  const [heading, setHeading] = useState('What\'s on');
  const [isFiltered, setIsFiltered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(currentFilters.month.length > 0 ? getMonthName(currentFilters.month[0]) : null);
  const [months, setMonths] = useState(getMonths());
  const [queryYear, setQueryYear] = useState(currentFilters.year.length > 0 ? `20${currentFilters.year[0]}` : null);

  const loaded = React.useRef(false);

  const sortConcertsAscending = (arr) => {
    arr.sort((a, b) => {
      const aDate = convertTimestampToDate(a.date);
      const bDate = convertTimestampToDate(b.date);

      if (aDate < bDate) return -1;
      if (aDate > bDate) return 1;
      return 0;
    });
  };

  useEffect(() => {
    if (!cyop) {
      loaded.current = true;
      const formattedHeading = loaded && month && queryYear ? `What's on, ${month} ${queryYear}` : 'What\'s on';
      setHeading(formattedHeading);
    }
  }, [loaded, month, queryYear, cyop]);

  useEffect(() => {
    const { CancelToken } = axios;
    const source = CancelToken.source();

    if (!apiUrl) return;

    setLoading(true);

    const getData = async () => {
      try {
        axios.get(apiUrl, { cancelToken: source.token })
          .then((response) => {
            const responseData = cyop ? response.data.concerts : response.data.data;
            const formattedData = formatArrayOfObject(responseData);
            const availableMonthsMeta = response.data.meta.find((meta) => meta.id === 'availableMonths');
            if (availableMonthsMeta && availableMonthsMeta.options) {
              setMonths(getMonths(availableMonthsMeta.options));
            }
            sortConcertsAscending(formattedData);
            setConcerts(formattedData);
            setLoading(false);
            setError('');
          });
      } catch (err) {
        if (axios.isCancel(err)) {
          throw new Error('request cancelled!');
        } else {
          setHasCriticalError(true);
          setError(err);
        }
      }
    };

    if (cyop) {
      setIsFiltered(JSON.stringify(filterQueries) !== JSON.stringify(defaultFilterQueries));
    }

    getData();

    return () => { // eslint-disable-line consistent-return
      source.cancel();
    };
  }, [apiUrl, cyop]); // eslint-disable-line react-hooks/exhaustive-deps

  // useEffect that manages URL
  useEffect(() => {
    const parsedQueries = JSON.parse(filterQueriesState);
    const hasActiveFilters = Object.entries(parsedQueries).filter(entry => entry[1].length > 0).length > 0;

    if (hasActiveFilters) {
      updateLocationQuery(parsedQueries);

      if (!calendarWasPreopened) window.location.hash = '#calendar';
    } else if (!window.location.hash) {
      updateLocationQuery();

      if (!calendarWasPreopened) window.location.hash = '';
    }
  }, [calendarWasPreopened, filterQueriesState]);

  const resetFilters = (reload = false) => {
    const filterOptions = [...document.querySelectorAll('.js-filterbar [type="checkbox"]')];
    if (filterOptions.length) {
      filterOptions.forEach((option) => resetCheckbox(option));
    }

    if (cyop) {
      setApiUrl(apiMetaUrl);
      setIsFiltered(false);
    }

    if (reload) setApiUrl(removeQueryStringParams(apiUrl, true));

    const resetFiltersButKeepMonthObject = {
      ...structuredClone(defaultFilterQueries),
      month: filterQueries.month ?? [],
      year: filterQueries.year ?? [],
    };
    filterQueries = resetFiltersButKeepMonthObject;

    setFilterQueriesState(JSON.stringify(resetFiltersButKeepMonthObject));
  };

  const resetMonthsFilters = () => {
    const monthsFilter = [...document.querySelectorAll('.js-month-button')];

    if (monthsFilter.length) {
      monthsFilter.forEach((monthFilter) => monthFilter.classList.remove('is-active'));
    }

    filterQueries.month = [];
    filterQueries.year = [];

    setFilterQueriesState(prev => {
      const prevObj = JSON.parse(prev);

      return JSON.stringify({
        ...prevObj,
        month: [],
        year: [],
      });
    });
  };

  const toggleQueryParams = (checked, category, value) => {
    if (checked) {
      filterQueries[category].push(value);

      setFilterQueriesState(prev => {
        const prevObj = JSON.parse(prev);

        return JSON.stringify({
          ...prevObj,
          [category]: [...prevObj[category], value],
        });
      });
    } else {
      removeItemFromArray(filterQueries[category], value);

      setFilterQueriesState((prev => {
        const prevObj = JSON.parse(prev);
        const prevCategoryValue = prevObj[category];
        removeItemFromArray(prevCategoryValue, value);

        return JSON.stringify({
          ...prevObj,
          [category]: prevCategoryValue,
        });
      }));
    }
  };

  const filterConcertsMonthWise = (e) => {
    // Reset filter options/checkboxes
    resetFilters();

    const formattedUrl = removeQueryStringParams(apiUrl);
    const activeMonthName = e.target.innerText;
    const activeMonthId = e.target.id;
    const [activeMonth, activeYear] = activeMonthId.split('/');

    // 1 is added to match back end month index. Unlike FE, BE index count starts with 1 instead of 0
    const formattedActiveMonth = activeMonth.length < 2 ? `0${activeMonth}` : activeMonth;
    const formattedYear = activeYear.slice(2);

    if (e.target.classList.contains('is-active')) {
      if (activeMonthName === 'Soon') {
        filterQueries = structuredClone(defaultFilterQueries);

        setFilterQueriesState(JSON.stringify(filterQueries));

        setApiUrl(formattedUrl);
        setMonth('');
        setQueryYear('');
      } else {
        filterQueries.month = [formattedActiveMonth];
        filterQueries.year = [formattedYear];

        setFilterQueriesState(() => {
          const newState = {
            ...structuredClone(defaultFilterQueries),
            month: [formattedActiveMonth],
            year: [formattedYear],
          };

          return JSON.stringify(newState);
        });

        setApiUrl(`${formattedUrl}?month=${formattedActiveMonth}&year=${formattedYear}`);
        setMonth(getMonthName(activeMonth));
        setQueryYear(activeYear);
      }
    } else {
      filterQueries = structuredClone(defaultFilterQueries);

      setFilterQueriesState(JSON.stringify(filterQueries));

      setApiUrl(formattedUrl);
      setMonth('');
      setQueryYear('');
    }
  };

  const buildFilterQuery = (category, id, value, checked) => {
    const formattedUrl = removeQueryStringParams(apiUrl);

    switch (category) { //eslint-disable-line
      case 'concertType':
      case 'venue':
      case 'concertMood':
      case 'concertSeason':
      case 'concertInstrument':
      case 'concertSoloist':
      case 'concertComposer':
      case 'day':
        toggleQueryParams(checked, category, id);
        break;
      case 'time':
        toggleQueryParams(checked, category, value);
        break;
    }

    // Reset month filter
    resetMonthsFilters();

    setApiUrl(`${formattedUrl}?${buildSearchQueryFromFilters(filterQueries)}`);
  };

  const handleChange = (e) => {
    const {
      name, value, id, checked,
    } = e.target;

    buildFilterQuery(name, id, value, checked);
    setHeading('What\'s on');
  };

  queriedIds = Object.keys(filterQueries).map((key) => filterQueries[key]).flatMap((value) => value);

  if (hasCriticalError) {
    document.body.classList.add('has-modal-opened');

    return <Modal type="critical-error" />;
  }

  if (cyop) {
    return (
      <>
        <Section zeroBottomPadding cyopContainer>
          <h3 className="c-cyop__filterbar-heading">Filter By</h3>
          <span
            className="c-cyop__reset-filters"
            onClick={() => resetFilters()}
            onKeyDown={() => resetFilters()}
            role="button"
            tabIndex="0"
          >
            Reset all filters
          </span>
        </Section>

        <Section zeroBottomPadding cyopContainer cyopFilterBar>
          <FilterBar apiUrl={apiUrl} handleChange={handleChange} value={queriedIds} cyop />
        </Section>

        {loading && <div className="c-loader c-loader--cyop"><Loader /></div>}
        {error && <div>{error}</div>}

        {!loading && !isFiltered
          && <ConcertsGroupedByMonth concerts={concerts} setModal={setModal} />}
        {!loading && isFiltered
          && <ConcertsSortedByDate concerts={concerts} setModal={setModal} />}
      </>
    );
  }

  return (
    <>
      <div className="c-calendar__logo-mobile"><Logo /></div>

      <Section zeroBottomPadding isFixed paddingLeftXlarge hasBgWhite header offsetLarge>
        <Header heading={heading} month={month} year={queryYear} />
      </Section>

      <SideBar
        filterConcerts={(e) => filterConcertsMonthWise(e)}
        months={months}
      />

      <Section zeroBottomPadding isFixed isFixedSecondary paddingLeftXlarge hasBorderBottom hasBgGrey offsetSmall>
        <FilterBar apiUrl={apiUrl} handleChange={handleChange} value={queriedIds} resetFilters={resetFilters} />
      </Section>

      <div className="c-calendar__card-list-wrapper">
        {loading && <div className="c-loader  c-loader--dark  c-loader--absolute"><Loader /></div>}
        {error && <div>{error}</div>}
        {concerts && concerts.length > 0 && !loading && (
          <ConcertCardList
            ref={(CalCardList) => {
              window.CalCardList = CalCardList;
            }}
            concerts={concerts}
            loading={loading}
            apiUrl={apiUrl}
          />
        )}
        {(!concerts || !concerts.length) && !loading && (
          <div className="u-absolute-position@medium  u-text-align-centre  u-spacing-top-large@small"><p className="u-heading-3">No concerts found</p></div>
        )}
      </div>
    </>
  );
};

Calendar.defaultProps = {
  cyop: false,
  setModal: undefined,
};

Calendar.propTypes = {
  cyop: PropTypes.bool,
  setModal: PropTypes.func,
};

export default Calendar;
